import "../../../styles/Shared.css";
import "../../../styles/Modals.css";
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import { useState, useEffect } from 'react'
import { FiAlertCircle } from "react-icons/fi";

import { ModalStates } from './ResolveMissingDataModalStates';
import { FirstNameMaxLength, LastNameMaxLength, PatientBirthWeightLimits, PatientBirthWeightMaxValue, PatientBirthWeightMinValue, PatientGestationalAgeMaxValue, PatientGestationalAgeMinValue, PatientGestationalAgeStep } from "../../../utilities/Constants";
import ResolveMissingInformation from "../../../data_layer/repositories/Patients/ResolveMissingInformation";
import { WeightUnits } from "../../../utilities/WeightUnits";

import { InputValidationErrorStates, HasErrors, CheckIsMissingDecimalMinMaxValue } from '../UpdateEntityModals/InputValidation';
import UpdateEntityModalHeader from '../UpdateEntityModals/UpdateEntityModalHeader';
import UpdateEntityModalFooter from '../UpdateEntityModals/UpdateEntityModalFooter';
import UpdateEntityModalDataLine from '../UpdateEntityModals/UpdateEntityModalDataLine';
import UpdateEntityModalDataLineConceptualAgeEdit from '../UpdateEntityModals/UpdateEntityModalDataLineConceptualAgeEdit';
import UpdateEntityModalDataLineAndPicker from '../UpdateEntityModals/UpdateEntityModalDataLineAndPicker';
import UpdateEntityModalEditConfirmDataLine from '../UpdateEntityModals/UpdateEntityModalEditConfirmDataLine';

function FieldRow({ isMissing, children }) {
  return (
    <div className="row align-items-center">
      <div className="col-sm-1 col-form-label">
        <FiAlertCircle className='DataRowIcon' style={{ visibility: isMissing ? 'visible' : 'hidden' }} />
      </div>
      <div className="col-sm-11">
        {children}
      </div>
    </div>
  );
}

function ResolveMissingDataModal(props) {

  const defaultResultMessage = "Writing...";

  const [modalState, setModalState] = useState(ModalStates.Edit);
  const [editedPatient, setEditedPatient] = useState({});
  const [originalPatient, setOriginalPatient] = useState({});
  const [resultMessage, setResultMessage] = useState(defaultResultMessage);
  const [formEdited, setFormEdited] = useState(false);
  const [errorStates, setErrorStates] = useState({
    gestationalAge: InputValidationErrorStates.NoError,
    conceptionalAge: InputValidationErrorStates.NoError,
    birth_weight: InputValidationErrorStates.NoError,
  });

  useEffect(() => {
    setEditedPatient(props.patient);
    setOriginalPatient(props.patient);
  }, [props.patient]);

  function updateEditedParameter(newValue, parameter) {
    setFormEdited(true);
    setErrorStates(prev => ({ ...prev, [parameter]: InputValidationErrorStates.NoError }));
    setEditedPatient(prev => ({ ...prev, [parameter]: newValue }));
  }

  function onSelectChanged(newValue, parameter) {
    setFormEdited(true);
    if (parameter === 'weight_uom' && props.dataWasMissing?.birth_weight) {
      const weightLimits = PatientBirthWeightLimits[newValue] ?? PatientBirthWeightLimits.lb;
      const birthWeightError = CheckIsMissingDecimalMinMaxValue(editedPatient.birth_weight, weightLimits.min, weightLimits.max);
      setErrorStates(prev => ({ ...prev, birth_weight: birthWeightError }));
    }
    setEditedPatient(prev => ({ ...prev, [parameter]: newValue }));
  }

  function validateGestationalAge(value) {
    if (!value || value == 0) return InputValidationErrorStates.RequiredAndMissing;
    if (parseFloat(value) < PatientGestationalAgeMinValue) return InputValidationErrorStates.IncorrectMinValue;
    if (parseFloat(value) > PatientGestationalAgeMaxValue) return InputValidationErrorStates.IncorrectMaxValue;
    return InputValidationErrorStates.NoError;
  }

  function validateInputs() {
    const gestationalAgeError = props.dataWasMissing?.gestationalAge
      ? validateGestationalAge(editedPatient.gestationalAge)
      : InputValidationErrorStates.NoError;

    const conceptionalAgeError = props.dataWasMissing?.dateOfConception
      ? ((!editedPatient.conceptionalAge || editedPatient.conceptionalAge == 0)
        ? InputValidationErrorStates.RequiredAndMissing
        : InputValidationErrorStates.NoError)
      : InputValidationErrorStates.NoError;

    let birthWeightError = InputValidationErrorStates.NoError;
    if (props.dataWasMissing?.birth_weight) {
      const weightLimits = PatientBirthWeightLimits[editedPatient.weight_uom] ?? PatientBirthWeightLimits.lb;
      birthWeightError = CheckIsMissingDecimalMinMaxValue(editedPatient.birth_weight, weightLimits.min, weightLimits.max);
    }

    setErrorStates({ gestationalAge: gestationalAgeError, conceptionalAge: conceptionalAgeError, birth_weight: birthWeightError });

    return !HasErrors([gestationalAgeError, conceptionalAgeError, birthWeightError]);
  }

  async function updateEntity() {
    await ResolveMissingInformation(editedPatient, setResultMessage)
      .then(props.updateAlerts);
  }

  function closeModal() {
    setFormEdited(false);
    setResultMessage(defaultResultMessage);
    setErrorStates({
      gestationalAge: InputValidationErrorStates.NoError,
      conceptionalAge: InputValidationErrorStates.NoError,
      birth_weight: InputValidationErrorStates.NoError,
    });
    props.close();
    setModalState(ModalStates.Edit);
  }

  return (
    <div
      className="modal show"
      style={{ display: 'block', position: 'initial' }}
    >
      <Modal
        show={props.show}
        backdrop="static"
        centered
        size="xl">

        <UpdateEntityModalHeader
          headerLabel="Update missing data"
          close={closeModal} />

        <Modal.Body>
          <label className={modalState === ModalStates.ResultMessage ? 'ResultMessage' : 'InvisibleCollapse'}>{resultMessage}</label>
          <Container className={modalState === ModalStates.ResultMessage ? 'InvisibleCollapse' : 'null'}>

            <p className="neo_gen_text mt-1 mb-3">Only missing fields are editable here. To edit other fields, go to the patient profile.</p>

            <FieldRow isMissing={false}>
              <UpdateEntityModalDataLine
                modalState={modalState}
                labelName="PATIENT ID"
                parameterName='ehrReference'
                value={editedPatient.ehrReference}
                disabled />
            </FieldRow>

            <FieldRow isMissing={!!props.dataWasMissing?.firstName}>
              <UpdateEntityModalDataLine
                modalState={modalState}
                labelName="FIRST INITIAL"
                parameterName='firstName'
                value={editedPatient.firstName}
                entryMaxLength={FirstNameMaxLength}
                inputChanged={updateEditedParameter}
                disabled={!props.dataWasMissing?.firstName} />
            </FieldRow>

            <FieldRow isMissing={!!props.dataWasMissing?.lastName}>
              <UpdateEntityModalDataLine
                modalState={modalState}
                labelName="LAST NAME"
                parameterName='lastName'
                value={editedPatient.lastName}
                entryMaxLength={LastNameMaxLength}
                inputChanged={updateEditedParameter}
                disabled={!props.dataWasMissing?.lastName} />
            </FieldRow>

            <FieldRow isMissing={!!props.dataWasMissing?.gestationalAge}>
              <UpdateEntityModalDataLine
                modalState={modalState}
                labelName="GESTATIONAL AGE"
                parameterName='gestationalAge'
                value={editedPatient.gestationalAge == 0 ? '' : editedPatient.gestationalAge}
                required={props.dataWasMissing?.gestationalAge}
                type='number'
                step={PatientGestationalAgeStep}
                min={PatientGestationalAgeMinValue}
                max={PatientGestationalAgeMaxValue}
                inputChanged={updateEditedParameter}
                errorState={errorStates.gestationalAge}
                disabled={!props.dataWasMissing?.gestationalAge} />
            </FieldRow>

            <FieldRow isMissing={!!props.dataWasMissing?.dateOfConception}>
              {//props.dataWasMissing?.dateOfConception ? (
                true ? (
                <UpdateEntityModalDataLineConceptualAgeEdit
                  modalState={modalState}
                  labelName="CONCEPTUAL AGE"
                  parameterName='conceptionalAge'
                  value={editedPatient.conceptionalAge == 0 ? '' : editedPatient.conceptionalAge}
                  required
                  type='number'
                  step='0.01'
                  inputChanged={updateEditedParameter}
                  errorState={errorStates.conceptionalAge}
                  gestationalAge={editedPatient.gestationalAge} 
                  disabled={!props.dataWasMissing?.dateOfConception} />
              ) : (
                <UpdateEntityModalDataLine
                  modalState={modalState}
                  labelName="CONCEPTUAL AGE"
                  parameterName='conceptionalAge'
                  value={editedPatient.conceptionalAge == 0 ? '' : editedPatient.conceptionalAge}
                  disabled />
              )}
            </FieldRow>

            <FieldRow isMissing={!!props.dataWasMissing?.birth_weight}>
              {props.dataWasMissing?.birth_weight ? (
                <UpdateEntityModalDataLineAndPicker
                  modalState={modalState}
                  labelName="BIRTH WEIGHT"
                  parameterName='birth_weight'
                  selectName='weight_uom'
                  value={editedPatient.birth_weight == 0 ? '' : editedPatient.birth_weight}
                  required
                  type='number'
                  step='0.1'
                  min={ (PatientBirthWeightLimits[editedPatient.weight_uom] ?? PatientBirthWeightLimits.lb).min }
                  max={ (PatientBirthWeightLimits[editedPatient.weight_uom] ?? PatientBirthWeightLimits.lb).max }
                  minDisplay={ `${(PatientBirthWeightLimits[editedPatient.weight_uom] ?? PatientBirthWeightLimits.lb).min} ${editedPatient.weight_uom ?? 'lb'}` }
                  maxDisplay={ `${(PatientBirthWeightLimits[editedPatient.weight_uom] ?? PatientBirthWeightLimits.lb).max} ${editedPatient.weight_uom ?? 'lb'}` }
                  inputChanged={updateEditedParameter}
                  valueForSelect={editedPatient.weight_uom}
                  choices={WeightUnits}
                  selectChange={onSelectChanged}
                  errorState={errorStates.birth_weight} />
              ) : (
                <UpdateEntityModalDataLine
                  modalState={modalState}
                  labelName="BIRTH WEIGHT"
                  parameterName='birth_weight'
                  value={editedPatient.birth_weight == 0 ? '' : `${editedPatient.birth_weight} ${editedPatient.weight_uom}`}
                  disabled />
              )}
            </FieldRow>

          </Container>

          <div className={modalState === ModalStates.ConfirmEdit ? 'null' : 'InvisibleCollapse'}>
            <hr className='ThickLine'></hr>
            <label className="ConfirmHeader">Are you sure you want to save your changes?</label>
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="FIRST INITIAL"
              originalParameter={originalPatient.firstName}
              updatedParameter={editedPatient.firstName} />
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="LAST NAME"
              originalParameter={originalPatient.lastName}
              updatedParameter={editedPatient.lastName} />
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="GESTATIONAL AGE"
              originalParameter={originalPatient.gestationalAge}
              updatedParameter={editedPatient.gestationalAge} 
              unit="weeks"/>
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="CONCEPTUAL AGE"
              originalParameter={originalPatient.conceptionalAge}
              updatedParameter={editedPatient.conceptionalAge} 
              subtitle={`As of ${new Date().getMonth()+1}/${new Date().getDate()}/${new Date().getFullYear()}`}
              unit="weeks"/>
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="BIRTH WEIGHT"
              originalParameter={originalPatient.birth_weight}
              updatedParameter={editedPatient.birth_weight} />
            <UpdateEntityModalEditConfirmDataLine
              modalState={modalState}
              label="WEIGHT UNIT"
              originalParameter={originalPatient.weight_uom}
              updatedParameter={editedPatient.weight_uom} />
          </div>
        </Modal.Body>

        <UpdateEntityModalFooter
          modalState={modalState}
          setState={setModalState}
          updateEntity={updateEntity}
          validateInputs={validateInputs}
          close={closeModal}
          active={formEdited} />

      </Modal>
    </div>
  );
}

export default ResolveMissingDataModal;
